/**
 * My week — a person's own time, leave and disbursements for one week
 * (T&B-1 PR 2). Days across with what the working week expects; entries
 * below, each a draft the author may change or delete until it is submitted.
 *
 * PR 3: "Submit week" sends every draft and returned item of the week to a
 * manager; they become read-only until approved, or returned with a reason
 * (shown on the entry, which the author then corrects and submits again).
 *
 * Expected hours come from the person's working pattern less public holidays
 * (decision 8), worked out by the server. When no working week is set the
 * page says so instead of showing zero. No rand values for time here: rates
 * and values are for managers (decision 5). A disbursement's amount is shown,
 * because the person typed it.
 */

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  api,
  type AuthState,
  type BillingJob,
  type ClientRef,
  type DisbursementRow,
  type ObligationTypeInUse,
  type TimeEntryRow,
  type WeekResponse,
} from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState, LoadingState } from '../components/States'
import { SuggestionList } from './SuggestionsPage'
import {
  bodyFromDraft,
  ClientPicker,
  clientLabel,
  draftFromEntry,
  EntryFields,
  inputCls,
  labelCls,
  primaryBtn,
  quietBtn,
  rand,
  StatusChip,
  todaySast,
  useBillingJobs,
  type EntryDraft,
} from '../components/TimeCapture'

const sectionCls = 'mb-4 rounded-eq-md border border-eq-border bg-white px-5 py-4'
const h2Cls = 'mb-2 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral'
const thCls = 'px-2 py-1 text-left font-semibold'
const tdCls = 'px-2 py-1.5 align-top'

const dayHead = (d: string) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })

function addDaysIso(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
function mondayOfIso(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  return addDaysIso(iso, -((d.getUTCDay() + 6) % 7))
}

type Act = (fn: () => Promise<unknown>, done: string) => Promise<boolean>

export function MyWeekPage({ obligationTypes, auth }: { obligationTypes: ObligationTypeInUse[]; auth: AuthState }) {
  const [params, setParams] = useSearchParams()
  const start = mondayOfIso(params.get('start') ?? todaySast())
  const [week, setWeek] = useState<WeekResponse | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { jobs, error: jobsError } = useBillingJobs()

  const load = useCallback(async () => {
    try {
      setWeek(await api.timeWeek(start))
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e))
    }
  }, [start])
  useEffect(() => {
    void load()
  }, [load])

  const act: Act = async (fn, done) => {
    if (busy) return false
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await fn()
      setNotice(done)
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      await load()
      setBusy(false)
    }
  }

  const go = (weeks: number) => setParams({ start: addDaysIso(start, 7 * weeks) })

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Time"
      title="My week"
      subtitle="Your time, leave and disbursements. Drafts are yours to change; submit the week when it is done, and a manager approves it."
      action={
        <span className="flex items-center gap-2">
          <button className={quietBtn} onClick={() => go(-1)}>
            ← Previous
          </button>
          <button className={quietBtn} onClick={() => setParams({})}>
            This week
          </button>
          <button className={quietBtn} onClick={() => go(1)}>
            Next →
          </button>
        </span>
      }
    >
      {auth.mode === 'anonymous' ? (
        <ErrorState message="Time is recorded per person, so My week needs sign-in. This server is running without Cognito (local development)." />
      ) : loadError ? (
        <ErrorState message={loadError} />
      ) : week === null || jobs === null ? (
        <LoadingState what="your week" />
      ) : (
        <>
          {jobsError && <ErrorState message={jobsError} />}
          {error && (
            <div className="mb-4">
              <ErrorState message={error} />
            </div>
          )}
          {notice && <div className="mb-4 rounded-eq-md border border-eq-border bg-white px-4 py-2 text-[13.5px]">{notice}</div>}

          {week.overdueWeeks.length > 0 && (
            <div className="mb-4 rounded-eq-md border border-eq-warning bg-eq-warning-bg px-4 py-2 text-[13.5px] text-eq-warning">
              {week.overdueWeeks.length === 1 ? 'Your timesheet for the week of ' : 'Your timesheets for the weeks of '}
              {week.overdueWeeks.map((w, i) => (
                <span key={w.weekStart}>
                  {i > 0 && (i === week.overdueWeeks.length - 1 ? ' and ' : ', ')}
                  <button type="button" className="underline" onClick={() => setParams({ start: w.weekStart })}>
                    {dayHead(w.weekStart)}
                  </button>
                </span>
              ))}{' '}
              {week.overdueWeeks.length === 1 ? 'is' : 'are'} past due and not submitted. Nothing is blocked — submit when you can.
            </div>
          )}
          <WeekSummary week={week} />
          <Submission week={week} act={act} busy={busy} />
          {/* T&B-1 PR 5: this week's suggestions, when there are any. Accepting one reloads the week. */}
          <WeekSuggestions key={week.weekStart} week={week} onChanged={() => void load()} />
          <Entries week={week} jobs={jobs} act={act} busy={busy} />
          <Disbursements week={week} act={act} busy={busy} />
        </>
      )}
    </AppShell>
  )
}

function WeekSummary({ week }: { week: WeekResponse }) {
  return (
    <section className={sectionCls}>
      <h2 className={h2Cls}>
        Week of {dayHead(week.weekStart)} – {dayHead(week.weekEnd)}
      </h2>
      {week.patternMissing && (
        <p className="mb-2 mt-0 text-[12.5px] text-eq-helper">
          No working week is set for you, so expected hours are not shown. A manager sets it under Jobs and prices → Rates and hours.
        </p>
      )}
      <table className="w-full border-collapse text-[13.5px] tabular-nums">
        <thead className="text-[12px] text-eq-neutral">
          <tr>
            <th className={thCls}></th>
            {week.days.map((d) => (
              <th key={d.date} className={[thCls, d.date === week.today ? 'text-eq-navy' : ''].join(' ')}>
                {dayHead(d.date)}
                {d.holiday && <div className="font-normal text-eq-helper">{d.holiday}</div>}
              </th>
            ))}
            <th className={thCls}>Week</th>
          </tr>
        </thead>
        <tbody>
          {(
            [
              ['Expected', (d: WeekResponse['days'][number]) => d.expected ?? '—', week.totals.expected ?? '—'],
              ['Logged', (d: WeekResponse['days'][number]) => d.logged, week.totals.logged],
              ['of which chargeable', (d: WeekResponse['days'][number]) => d.chargeable, week.totals.chargeable],
              ['of which leave', (d: WeekResponse['days'][number]) => d.leave, week.totals.leave],
            ] as const
          ).map(([label, cell, total]) => (
            <tr key={label} className="border-t border-eq-border">
              <td className={`${tdCls} text-eq-neutral`}>{label}</td>
              {week.days.map((d) => {
                const short = label === 'Logged' && d.expectedHundredths !== null && Math.round(Number(d.logged) * 100) < d.expectedHundredths
                return (
                  <td key={d.date} className={[tdCls, short && d.date < week.today ? 'text-eq-danger' : ''].join(' ')}>
                    {cell(d)}
                  </td>
                )
              })}
              <td className={`${tdCls} font-semibold`}>{total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

const whenLabel = (iso: string) =>
  new Date(iso).toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

function Submission({ week, act, busy }: { week: WeekResponse; act: Act; busy: boolean }) {
  const s = week.submission
  const last = s.submittedAt[s.submittedAt.length - 1]
  const closed = s.closedMonths.map((m) => new Date(`${m}T00:00:00`).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' }))
  const started = week.weekStart <= week.today
  return (
    <section className={`${sectionCls} flex flex-wrap items-center justify-between gap-3`}>
      <div className="text-[13.5px]">
        {last === undefined ? (
          <span>{started ? 'This week has not been submitted yet.' : 'This week has not started.'}</span>
        ) : (
          <span>
            Submitted {whenLabel(last)}
            {s.submittedAt.length > 1 && <span className="text-eq-helper"> (first {whenLabel(s.submittedAt[0]!)})</span>}.
          </span>
        )}
        {s.toSubmit > 0 && last !== undefined && (
          <span> {s.toSubmit} {s.toSubmit === 1 ? 'item is' : 'items are'} not submitted yet.</span>
        )}
        {s.returned > 0 && (
          <div className="mt-1 text-eq-warning">
            A manager returned {s.returned} {s.returned === 1 ? 'item' : 'items'} — see the reason on each, correct it, and submit again.
          </div>
        )}
        {closed.length > 0 && (
          <div className="mt-1 text-eq-helper">{closed.join(' and ')} {closed.length === 1 ? 'is' : 'are'} closed: time dated in it can no longer change.</div>
        )}
      </div>
      {started && (
        <button
          className={primaryBtn(busy || !s.canSubmit)}
          disabled={busy || !s.canSubmit}
          title={s.canSubmit ? undefined : 'Nothing new to submit'}
          onClick={() => {
            const what =
              s.toSubmit === 0
                ? 'Submit this week with nothing recorded in it?'
                : `Submit ${s.toSubmit} ${s.toSubmit === 1 ? 'item' : 'items'}? ${s.toSubmit === 1 ? 'It becomes' : 'They become'} read-only until a manager approves or returns ${s.toSubmit === 1 ? 'it' : 'them'}.`
            if (window.confirm(what)) void act(() => api.submitWeek(week.weekStart), 'Week submitted.')
          }}
        >
          Submit week
        </button>
      )}
    </section>
  )
}

const changeable = (status: string) => status === 'draft' || status === 'returned'

function ReturnReason({ reason }: { reason: string | null }) {
  if (reason === null) return null
  return <div className="mt-0.5 text-[12px] text-eq-warning">Returned: {reason}</div>
}

function Entries({ week, jobs, act, busy }: { week: WeekResponse; jobs: BillingJob[]; act: Act; busy: boolean }) {
  const dates = week.days.map((d) => d.date)
  const defaultDate = dates.includes(week.today) ? week.today : dates[0]!
  const blank = (): EntryDraft => ({ workDate: defaultDate, hours: '', jobId: '', taskId: null, client: null, narrative: '' })
  const [adding, setAdding] = useState<EntryDraft>(blank)
  const [editing, setEditing] = useState<{ id: string; draft: EntryDraft } | null>(null)
  const leaveJob = jobs.find((j) => j.kind === 'leave' && !j.archived)

  useEffect(() => {
    setAdding(blank())
    setEditing(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week.weekStart])

  const ready = adding.jobId !== '' && adding.hours.trim() !== ''

  return (
    <section className={sectionCls}>
      <h2 className={h2Cls}>Time and leave</h2>
      <form
        className="mb-3 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          void act(() => api.createTimeEntry(bodyFromDraft(adding, jobs)), 'Added.').then(
            (ok) => ok && setAdding({ ...blank(), workDate: adding.workDate, jobId: adding.jobId, client: adding.client })
          )
        }}
      >
        <EntryFields draft={adding} setDraft={setAdding} jobs={jobs} dates={dates} />
        <button type="submit" className={primaryBtn(busy || !ready)} disabled={busy || !ready}>
          Add
        </button>
        {leaveJob && (
          <button
            type="button"
            className={quietBtn}
            disabled={busy}
            title="Fill in leave for the chosen day, for the hours your working week expects"
            onClick={() => {
              const day = week.days.find((d) => d.date === adding.workDate)
              setAdding({ ...adding, jobId: leaveJob.id, taskId: null, client: null, hours: day?.expected && day.expected !== '0' ? day.expected : adding.hours })
            }}
          >
            Leave…
          </button>
        )}
      </form>

      {week.entries.length === 0 ? (
        <p className="m-0 text-[14px] text-eq-neutral">Nothing logged this week yet.</p>
      ) : (
        <table className="w-full border-collapse text-[13.5px]">
          <thead className="text-[12px] text-eq-neutral">
            <tr>
              <th className={thCls}>Day</th>
              <th className={thCls}>Hours</th>
              <th className={thCls}>Billing job</th>
              <th className={thCls}>Client</th>
              <th className={thCls}>Narrative</th>
              <th className={thCls}></th>
            </tr>
          </thead>
          <tbody>
            {week.entries.map((e: TimeEntryRow) =>
              editing?.id === e.id ? (
                <tr key={e.id} className="border-t border-eq-border bg-eq-grey">
                  <td colSpan={6} className="px-2 py-2">
                    <form
                      className="flex flex-wrap items-end gap-3"
                      onSubmit={(ev) => {
                        ev.preventDefault()
                        void act(() => api.updateTimeEntry(e.id, bodyFromDraft(editing.draft, jobs)), 'Saved.').then(
                          (ok) => ok && setEditing(null)
                        )
                      }}
                    >
                      <EntryFields draft={editing.draft} setDraft={(draft) => setEditing({ id: e.id, draft })} jobs={jobs} dates={dates} />
                      <button type="submit" className={primaryBtn(busy)} disabled={busy}>
                        Save
                      </button>
                      <button type="button" className="text-[12.5px] underline" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                    </form>
                  </td>
                </tr>
              ) : (
                <tr key={e.id} className="border-t border-eq-border">
                  <td className={`${tdCls} whitespace-nowrap`}>{dayHead(e.workDate)}</td>
                  <td className={`${tdCls} tabular-nums`}>
                    {e.hours}
                    {e.source === 'timer' && (
                      <div
                        className="text-[11px] text-eq-helper"
                        title={e.timer ? `Timer ran ${Math.round(e.timer.seconds / 60)} min, rounded up` : undefined}
                      >
                        timer
                      </div>
                    )}
                  </td>
                  <td className={tdCls}>
                    {e.job.name}
                    {e.task && <span className="text-eq-helper"> · {e.task.name}</span>}
                    {e.job.kind === 'leave' && <span className="ml-1 rounded-eq-pill bg-eq-ice px-2 py-0.5 text-[11px]">leave</span>}
                    {e.job.kind === 'work' && !e.job.chargeable && <div className="text-[11px] text-eq-helper">not chargeable</div>}
                  </td>
                  <td className={tdCls}>{e.client ? clientLabel(e.client) : '—'}</td>
                  <td className={`${tdCls} max-w-[22rem]`}>
                    {e.narrative ?? ''}
                    <ReturnReason reason={e.returnReason} />
                  </td>
                  <td className={`${tdCls} whitespace-nowrap text-right`}>
                    {changeable(e.status) ? (
                      <span className="inline-flex items-center gap-2">
                        {e.status === 'returned' && <StatusChip status={e.status} />}
                        <button className={quietBtn} disabled={busy} onClick={() => setEditing({ id: e.id, draft: draftFromEntry(e) })}>
                          Edit
                        </button>
                        <button
                          className="text-[12.5px] text-eq-danger underline"
                          disabled={busy}
                          onClick={() => {
                            if (window.confirm(`Delete ${e.hours} h on ${e.job.name}?`)) void act(() => api.deleteTimeEntry(e.id), 'Deleted.')
                          }}
                        >
                          Delete
                        </button>
                      </span>
                    ) : (
                      <StatusChip status={e.status} />
                    )}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      )}
    </section>
  )
}

function Disbursements({ week, act, busy }: { week: WeekResponse; act: Act; busy: boolean }) {
  const dates = week.days.map((d) => d.date).filter((d) => d <= week.today)
  const [date, setDate] = useState(dates[dates.length - 1] ?? '')
  const [client, setClient] = useState<ClientRef | null>(null)
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    setDate(dates[dates.length - 1] ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week.weekStart])

  const ready = date !== '' && client !== null && amount.trim() !== '' && description.trim() !== ''

  return (
    <section className={sectionCls}>
      <h2 className={h2Cls}>Disbursements</h2>
      <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
        Money paid on a client's behalf — a CIPC fee, a courier — recorded at cost, excluding VAT, once it is paid.
      </p>
      {dates.length > 0 && (
        <form
          className="mb-3 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            void act(
              () => api.createDisbursement({ incurredDate: date, amount, clientId: client!.id, description }),
              'Disbursement added.'
            ).then((ok) => ok && (setAmount(''), setDescription('')))
          }}
        >
          <label className={labelCls}>
            Date
            <select className={inputCls} value={date} onChange={(e) => setDate(e.target.value)}>
              {dates.map((d) => (
                <option key={d} value={d}>
                  {dayHead(d)}
                </option>
              ))}
            </select>
          </label>
          <label className={labelCls}>
            Client
            <ClientPicker value={client} onChange={setClient} />
          </label>
          <label className={labelCls}>
            Amount (R)
            <input className={`${inputCls} w-24 text-right`} value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label className={labelCls}>
            What for
            <input className={`${inputCls} w-64`} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <button type="submit" className={primaryBtn(busy || !ready)} disabled={busy || !ready}>
            Add
          </button>
        </form>
      )}
      {week.disbursements.length === 0 ? (
        <p className="m-0 text-[14px] text-eq-neutral">No disbursements this week.</p>
      ) : (
        <table className="w-full border-collapse text-[13.5px]">
          <tbody>
            {week.disbursements.map((d: DisbursementRow) => (
              <tr key={d.id} className="border-t border-eq-border">
                <td className={`${tdCls} whitespace-nowrap`}>{dayHead(d.incurredDate)}</td>
                <td className={tdCls}>{clientLabel(d.client)}</td>
                <td className={`${tdCls} tabular-nums`}>{rand(d.amountCents)}</td>
                <td className={tdCls}>
                  {d.description}
                  <ReturnReason reason={d.returnReason} />
                </td>
                <td className={`${tdCls} whitespace-nowrap text-right`}>
                  {d.status !== 'draft' && <StatusChip status={d.status} />}
                  {changeable(d.status) && (
                    <button
                      className="ml-2 text-[12.5px] text-eq-danger underline"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm(`Delete the ${rand(d.amountCents)} disbursement?`)) void act(() => api.deleteDisbursement(d.id), 'Deleted.')
                      }}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function WeekSuggestions({ week, onChanged }: { week: WeekResponse; onChanged: () => void }) {
  if (week.weekStart > week.today) return null
  return (
    <SuggestionList
      from={week.weekStart}
      to={week.weekEnd < week.today ? week.weekEnd : week.today}
      onChanged={onChanged}
      hideWhenEmpty
      heading="Suggested from your work this week"
      reloadToken={week.entries.map((e) => `${e.id}:${e.workDate}:${e.hundredths}:${e.client?.id ?? ''}:${e.job.id}`).join('|')}
    />
  )
}
